import {
  Box,
  Text,
  VStack,
  SimpleGrid,
  HStack,
  Badge,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { RequestOption } from '@/types/ticket'

type Asset = {
  id: number
  tag_number?: string | null
  model?: string | null
  serial_number?: string | null
  brand_name?: string | null
  category_name?: string | null
  function?: string | null
  [key: string]: any
}

type TicketData = {
  ticketNumber?: string | null
  description?: string | null
  notes?: string | null
  workPermit?: string | null
  scheduledAt?: string | null
  assets?: Asset[]
  subject?: string | null
  type?: string | null
  source?: string | null
  priority?: string | null
  status?: string | null
}

type Props = {
  /** Full request object from the API (ticket_requests table) */
  request?: RequestOption | null
  /** Parent ticket data (parent_tickets table) */
  ticket?: TicketData | null

  /** Legacy props for backward compat with ParentTicketDetail page */
  device?: string | null
  serial?: string | null
  service?: string | null
  poNo?: string | null
  warranty?: string | null
  billable?: string | null
  warrantyOptions?: { label: string; value: string }[]
  billableOptions?: { label: string; value: string }[]
  onChangeWarranty?: (value: string) => void
  onChangeBillable?: (value: string) => void
  isReadOnly?: boolean
}

function Pair({ label, value }: { label: string; value?: string | null }) {
  const colors = useThemeColors()
  return (
    <Box>
      <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={0.5}>
        {label}
      </Text>
      <Text fontSize="sm" color={colors.text} fontWeight="500">
        {value || '—'}
      </Text>
    </Box>
  )
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

const WORK_PERMIT_LABELS: Record<string, string> = {
  no_need: 'No Need',
  waiting: 'Waiting',
  have_permit: 'Have Permit',
}

const TYPE_LABELS: Record<string, string> = {
  onsite: 'Onsite',
  remote: 'Remote',
  chat_email: 'Chat/Email',
  // Legacy values (backward compatibility)
  incident: 'Incident',
  request: 'Request',
  problem: 'Problem',
  training_request: 'Training Request',
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  const colors = useThemeColors()
  return (
    <Text fontSize="xs" fontWeight="700" color={colors.textSecondary} textTransform="uppercase" letterSpacing="wider">
      {children}
    </Text>
  )
}

export default function TicketProperties({ request, ticket, device, serial, service, poNo }: Props) {
  const colors = useThemeColors()

  // Rich view when we have ticket data (View page)
  if (request || ticket) {
    const serviceLabel = request
      ? [request.serviceParent, request.serviceType].filter(Boolean).join(' → ')
      : undefined

    const assets = ticket?.assets ?? []
    const hasAssets = assets.length > 0

    return (
      <Box
        bg={colors.cardBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={colors.border}
        overflow="hidden"
      >
        {/* ─── Header ─── */}
        <Box px={4} py={3} borderBottom="1px solid" borderColor={colors.borderLight}>
          <Text fontWeight="700" color={colors.primary}>
            Ticket Details
          </Text>
        </Box>

        <Box px={4} py={4}>
          <VStack align="stretch" spacing={5}>

            {/* ════════════════════════════════════════════
                SECTION 1: Request Details
                (from ticket_requests table)
               ════════════════════════════════════════════ */}
            {request && (
              <Box>
                <HStack justify="space-between" mb={2}>
                  <SectionHeader>Request Details</SectionHeader>
                  {request.id && (
                    <Badge fontSize="xs" bg="purple.50" color="purple.700" border="1px solid" borderColor="purple.200" borderRadius="md" px={2.5} py={0.5} fontWeight="700">
                      {request.id}
                    </Badge>
                  )}
                </HStack>

                <VStack align="stretch" spacing={4}>
                  {/* Contact Information */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={8} spacingY={3}>
                    <Pair label="Contact Name" value={request.contactName} />
                    <Pair label="Contact Email" value={request.contactEmail} />
                    <Pair label="Contact Number" value={request.contactNumber} />
                  </SimpleGrid>

                  {/* Service & Classification */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={8} spacingY={3}>
                    <Pair label="Service Type" value={serviceLabel || undefined} />
                    <Pair label="Request Source" value={request.requestSource} />
                    <Box>
                      <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={0.5}>
                        Categories
                      </Text>
                      {request.categories && request.categories.length > 0 ? (
                        <HStack spacing={1} flexWrap="wrap">
                          {request.categories.map((cat) => (
                            <Badge
                              key={cat}
                              fontSize="xs"
                              colorScheme="blue"
                              variant="subtle"
                              borderRadius="md"
                              px={2}
                            >
                              {cat}
                            </Badge>
                          ))}
                        </HStack>
                      ) : (
                        <Text fontSize="sm" color={colors.text} fontWeight="500">—</Text>
                      )}
                    </Box>
                  </SimpleGrid>

                  {/* Schedule & Timeline */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={8} spacingY={3}>
                    <Pair label="Requested Quantity" value={request.requestedQuantity?.toString()} />
                    <Pair label="Preferred Schedule" value={formatDate(request.preferredScheduleAt)} />
                    <Pair label="Preferred Contact At" value={formatDate(request.preferredContactAt)} />
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={8} spacingY={3}>
                    <Pair label="Submitted" value={formatDate(request.submittedAt)} />
                    <Pair label="Acknowledged" value={formatDate(request.acknowledgedAt)} />
                    <Pair label="Requested At" value={formatDate(request.requestedAt)} />
                  </SimpleGrid>

                  {/* Request Description (only if different from ticket description) */}
                  {request.description && request.description !== ticket?.description && (
                    <Box>
                      <Text fontSize="xs" color={colors.textSecondary} fontWeight="600" mb={1}>
                        Request Description
                      </Text>
                      <Text fontSize="sm" color={colors.text} whiteSpace="pre-wrap" lineHeight="1.7">
                        {request.description}
                      </Text>
                    </Box>
                  )}
                </VStack>
              </Box>
            )}

            {/* ════════════════════════════════════════════
                SECTION 2: Parent Ticket Details
                (from parent_tickets table)
               ════════════════════════════════════════════ */}
            {ticket && (
              <>
                {request && <Divider borderColor={colors.borderLight} borderWidth="2px" />}
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <SectionHeader>Ticket Information</SectionHeader>
                    {ticket.ticketNumber && (
                      <Badge fontSize="xs" bg="blue.50" color="blue.700" border="1px solid" borderColor="blue.200" borderRadius="md" px={2.5} py={0.5} fontWeight="700">
                        {ticket.ticketNumber}
                      </Badge>
                    )}
                  </HStack>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={8} spacingY={3}>
                    <Pair label="Priority" value={ticket.priority} />
                    <Pair label="Support Type" value={ticket.type ? (TYPE_LABELS[ticket.type] ?? ticket.type) : undefined} />
                    <Pair label="Source" value={ticket.source} />
                    <Pair label="Work Permit" value={ticket.workPermit ? (WORK_PERMIT_LABELS[ticket.workPermit] ?? ticket.workPermit) : undefined} />
                    <Pair label="Scheduled At" value={formatDate(ticket.scheduledAt)} />
                  </SimpleGrid>
                </Box>
              </>
            )}

            {/* Description (parent_tickets.description) */}
            {ticket?.description && (
              <>
                <Divider borderColor={colors.borderLight} />
                <Box>
                  <SectionHeader>Description</SectionHeader>
                  <Text fontSize="sm" color={colors.text} whiteSpace="pre-wrap" lineHeight="1.7" mt={2}>
                    {ticket.description}
                  </Text>
                </Box>
              </>
            )}

            {/* Notes (parent_tickets.notes) */}
            {ticket?.notes && (
              <>
                <Divider borderColor={colors.borderLight} />
                <Box>
                  <SectionHeader>Notes</SectionHeader>
                  <Text fontSize="sm" color={colors.text} whiteSpace="pre-wrap" lineHeight="1.7" mt={2}>
                    {ticket.notes}
                  </Text>
                </Box>
              </>
            )}

            {/* Assets (parent_tickets.assets) */}
            {hasAssets && (
              <>
                <Divider borderColor={colors.borderLight} />
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <SectionHeader>Assets</SectionHeader>
                    <Badge fontSize="xs" colorScheme="gray" variant="subtle" borderRadius="full" px={2}>
                      {assets.length}
                    </Badge>
                  </HStack>
                  <TableContainer
                    maxH="260px"
                    overflowY="auto"
                    border="1px solid"
                    borderColor={colors.borderLight}
                    borderRadius="md"
                  >
                    <Table size="sm" variant="simple">
                      <Thead bg={colors.bg} position="sticky" top={0} zIndex={1}>
                        <Tr>
                          <Th fontSize="xs" py={2}>Tag #</Th>
                          <Th fontSize="xs" py={2}>Brand</Th>
                          <Th fontSize="xs" py={2}>Category</Th>
                          <Th fontSize="xs" py={2}>Model</Th>
                          <Th fontSize="xs" py={2}>Serial #</Th>
                          <Th fontSize="xs" py={2}>Function</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {assets.map((a) => (
                          <Tr key={a.id}>
                            <Td fontSize="xs" py={1.5}>{a.tag_number || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.brand_name || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.category_name || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.model || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.serial_number || '—'}</Td>
                            <Td fontSize="xs" py={1.5}>{a.function || '—'}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              </>
            )}
          </VStack>
        </Box>
      </Box>
    )
  }

  // Fallback: legacy view (ParentTicketDetail page)
  return (
    <Box
      bg={colors.cardBg}
      borderRadius="lg"
      border="1px solid"
      borderColor={colors.border}
      overflow="hidden"
    >
      <Box px={4} py={3} borderBottom="1px solid" borderColor={colors.borderLight}>
        <Text fontWeight="700" color={colors.primary}>
          Ticket Details
        </Text>
      </Box>

      <Box px={4} py={4}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={10} spacingY={4} w="100%">
          <Pair label="Device" value={device} />
          <Pair label="Serial" value={serial} />
          <Pair label="Service" value={service} />
          <Pair label="PO No." value={poNo} />
        </SimpleGrid>
      </Box>
    </Box>
  )
}
